import { useEffect, useState } from "react";
import { IoBarChart, IoCalendar, IoTime, IoDocumentText, IoTrendingUp } from "react-icons/io5";
import Grid from "@mui/material/Grid";
import MiniStatCard, { MiniStatCardSkeleton } from "components/MiniStatCard";
import PropTypes from "prop-types";
import axiosInstance from "services/httpInterceptor";



const PublishingStrategyCards = ({ companyId, marketAvgEngagement, topCategory }) => {

  const [insights, setInsights] = useState({
    mostActiveCompetitor: "Loading...",
    mostCommonPostingDay: "Loading...",
    mostCommonPostingTime: "Loading...",
    mostCommonPostType: "Loading...",
  });
  const [loading, setLoading] = useState(true); // Initial loading state is true
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInsights = async () => {
      // If critical parameters are not yet available, just return.
      // The component will remain in its current 'loading' state (initially true).
      if (!companyId) {
        return;
      }

      setError(null); // Reset error before a new attempt
      try {
        const params = { companyId };

        const response = await axiosInstance.get(
          `/v1/frontend/publishing/insights`, { params },
        );

        const newInsights = response.data;
        setInsights(currentInsights =>
          JSON.stringify(currentInsights) !== JSON.stringify(newInsights) ? newInsights : currentInsights,
        );
        setLoading(false); // Set loading to false on successful data fetch
      } catch (error) {
        console.error("Error fetching publishing insights:", error);
        setError("Data unavailable for publishing insights. Please try again later.");
        setInsights({
          mostActiveCompetitor: "-",
          mostCommonPostingDay: "-",
          mostCommonPostingTime: "-",
          mostCommonPostType: "-",
        });
        setLoading(false); // Set loading to false on error
      }
      // Removed finally block for setLoading(false)
    };

    fetchInsights();
  }, [companyId]);

  if (loading) {
    return (
      <Grid container spacing={3} px={2} sx={{ minHeight: "100px", alignItems: "flex-start" }}>
        {[0, 1, 2, 3].map((i) => (
          <Grid key={i} item xs={12} sm={6} md={3}>
            <MiniStatCardSkeleton />
          </Grid>
        ))}
      </Grid>
    );
  }

  // If error or no data, keep skeleton to avoid layout shift (same pattern as SocialMediaStats, NewsStats)
  if (error) {
    return (
      <Grid container spacing={3} px={2} sx={{ minHeight: "100px", alignItems: "flex-start" }}>
        {[0, 1, 2, 3].map((i) => (
          <Grid key={i} item xs={12} sm={6} md={3}>
            <MiniStatCardSkeleton />
          </Grid>
        ))}
      </Grid>
    );
  }

  // "Peak Content Time" is replaced by "Market Avg Engagement" when the parent
  // provides marketAvgEngagement (computed from the lifted /v2/general/stats fetch).
  const thirdCard = marketAvgEngagement != null
    ? {
        title: "Market Avg Engagement",
        value: marketAvgEngagement % 1 === 0
          ? `${marketAvgEngagement}`
          : `${marketAvgEngagement.toFixed(1)}`,
        iconComponent: <IoTrendingUp size="22px" color="white" />,
        iconColor: "info",
      }
    : {
        title: "Peak Content Time",
        value: insights.mostCommonPostingTime,
        iconComponent: <IoTime size="22px" color="white" />,
        iconColor: "info",
      };

  const cardsData = [
    {
      title: "Most Active Competitor",
      value: insights.mostActiveCompetitor,
      iconComponent: <IoBarChart size="22px" color="white" />,
      iconColor: "info",
    },
    {
      title: "Peak Content Day",
      value: insights.mostCommonPostingDay,
      iconComponent: <IoCalendar size="22px" color="white" />,
      iconColor: "info",
    },
    thirdCard,
    {
      title: "Effective Content",
      value: topCategory ?? insights.mostCommonPostType,
      iconComponent: <IoDocumentText size="22px" color="white" />,
      iconColor: "info",
    },
  ];

  return (
    // Grid container standardized to spacing={3}
    <Grid container spacing={3} px={2}
          sx={{ minHeight: "100px", alignItems: "flex-start" }}> {/* ADDED minHeight and alignItems */}
      {cardsData.map((item, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          {/* Use MiniStatCard instead of custom layout inside Card */}
          <MiniStatCard
            title={{ text: item.title }}
            count={item.value}
            icon={{
              color: item.iconColor,
              component: item.iconComponent,
            }}
          />
        </Grid>
      ))}
    </Grid>
  );
};

PublishingStrategyCards.propTypes = {
  companyId: PropTypes.string.isRequired,
  marketAvgEngagement: PropTypes.number,
  topCategory: PropTypes.string,
};

export default PublishingStrategyCards;